// Browser-side companion to the backend session middleware.
//
// The backend assigns an app_sessions.id on the first authenticated request
// and echoes it back as the `X-Session-Id` response header. We stash that id
// in sessionStorage and replay it on every subsequent request so the backend
// can keep us bound to the same row (instead of opening a new one on each
// page load and inflating the dashboard's session counter).
//
// We also generate a per-tab "client fingerprint" once and send it as
// `X-Client-Fingerprint`. The middleware folds it into the device fingerprint
// hash, which means closing a tab and opening a new one counts as a new
// session boundary even when the UA is identical — matches the agreed
// product behavior (30-min idle OR new device/tab).

const SESSION_KEY = "vd.session_id";
const FINGERPRINT_KEY = "vd.client_fp";
// Visitor cookie name — must match the backend middleware/visitor.go literal.
// Long-lived (90 days) and shared across tabs/sessions, so the admin can
// stitch a visitor's full pre-register journey across multiple visits.
const VISITOR_COOKIE = "vd_visitor_id";

/** Returns the stored session id, or undefined if we haven't received one yet. */
export function getSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem(SESSION_KEY) ?? undefined;
}

/** Stores the session id. Called from apiFetch after each response. */
export function setSessionId(id: string | undefined): void {
  if (typeof window === "undefined") return;
  if (!id) {
    window.sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  window.sessionStorage.setItem(SESSION_KEY, id);
}

/**
 * Returns a per-tab fingerprint, generating one on first use. We keep it in
 * sessionStorage (not localStorage) so each tab gets its own — closing and
 * reopening a tab triggers a new session.
 */
export function getClientFingerprint(): string {
  if (typeof window === "undefined") return "ssr";
  const cached = window.sessionStorage.getItem(FINGERPRINT_KEY);
  if (cached) return cached;
  const fp = crypto.randomUUID();
  window.sessionStorage.setItem(FINGERPRINT_KEY, fp);
  return fp;
}

/** Drop the session id (e.g. on explicit logout). */
export function clearSession(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(SESSION_KEY);
  window.sessionStorage.removeItem(FINGERPRINT_KEY);
}

/**
 * Returns the long-lived visitor id, generating + persisting one on first
 * use. Lookup order:
 *
 *   1. The `vd_visitor_id` cookie (backend issues + refreshes it on every
 *      onboarding/marketing route).
 *   2. localStorage `vd.visitor_id` — survives across tabs and TLS sessions.
 *   3. Generate a fresh UUID and persist to (2).
 *
 * The id is echoed as `X-Visitor-Id` on every API call. Cross-origin XHRs to
 * `*.run.app` don't auto-receive the cookie on the very first request; the
 * localStorage fallback lets us send a stable id from request #1, and the
 * backend's middleware honors the header over the cookie when both are
 * present, keeping pre/post-cookie events linked.
 */
const VISITOR_LS_KEY = "vd.visitor_id";

export function getVisitorId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${VISITOR_COOKIE}=([^;]+)`));
  if (cookieMatch) {
    const id = decodeURIComponent(cookieMatch[1]);
    // Mirror to localStorage so future visits with cleared cookies still
    // reuse the same identity.
    try {
      window.localStorage.setItem(VISITOR_LS_KEY, id);
    } catch {
      /* private mode / quota — non-fatal */
    }
    return id;
  }
  try {
    const cached = window.localStorage.getItem(VISITOR_LS_KEY);
    if (cached) return cached;
  } catch {
    /* fallthrough to generation */
  }
  const fresh = crypto.randomUUID();
  try {
    window.localStorage.setItem(VISITOR_LS_KEY, fresh);
  } catch {
    /* non-fatal — backend will issue a fresh cookie too */
  }
  return fresh;
}
